import { cn } from './cn.js';

/**
 * O link de navegação em texto — o "Voltar para a entrada", o "Esqueci minha
 * senha", o "Voltar para minhas demandas".
 *
 * ## Por que isto é um componente e não três classes repetidas
 *
 * Um `<a>` solto dentro de um parágrafo tem a altura da linha: **17px**. A
 * régua do `DESIGN.md` pede 44px onde há dedo (WCAG 2.5.5), e um link de texto
 * é alvo de dedo como qualquer outro — a diferença é que ele não *parece* um
 * controle, e por isso passa em revisão.
 *
 * Ele apareceu quatro vezes seguidas nas telas públicas do portal, sempre com o
 * mesmo defeito e sempre escrito à mão. Enquanto a correção for uma string que
 * cada tela copia, a quinta tela nasce errada. Aqui ela é uma decisão só.
 *
 * O alvo cresce **apenas em ponteiro grosso**, pelo mesmo motivo do `Button`:
 * no mouse, 44px de altura num link solto abre um buraco no meio do texto.
 *
 * Não é um componente React porque quem navega é o `Link` do TanStack Router, e
 * `@evogard/ui` não conhece roteador — do mesmo jeito que `buttonClasses` existe
 * para os primitivos do shadcn.
 *
 * ## Não ponha pontuação encostada nele
 *
 * O respiro lateral é parte do alvo, e no telefone ele afasta o que vier
 * depois: um ponto final logo após o link fica flutuando a doze pixels da
 * palavra e é lido como erro de digitação. Cancelar isso com margem negativa
 * resolveria a aparência e faria o toque no caractere seguinte cair dentro do
 * link — pior. Termine a frase no próprio link, ou ponha o link no fim dela.
 */
export function linkTextClasses(className?: string): string {
  return cn(
    'inline-flex items-center justify-center text-primaria-texto hover:underline',
    // o alvo, e o respiro lateral que o acompanha para o toque não cair fora
    'pointer-coarse:min-h-11 pointer-coarse:px-3',
    className,
  );
}

/**
 * O link de voltar, acima do título da tela.
 *
 * Sete telas o escreviam à mão como `text-xs text-texto-suave hover:underline`
 * com o caractere `←` colado no texto. Dois defeitos numa linha só: o glifo de
 * texto fazendo papel de ícone (o `DESIGN.md` bane, e agora existe
 * `seta-esquerda` no `Icon`), e um alvo de **16px** de altura onde a régua pede
 * 44 no dedo — o mesmo defeito que criou `linkTextClasses`, repetido no lugar
 * onde ninguém procurou porque ele não *parece* um controle.
 *
 * É mais discreto que `linkTextClasses` de propósito: voltar é a saída, não a
 * ação da tela. O que ele não abre mão é do alvo.
 */
export function backLinkClasses(className?: string): string {
  return cn(
    'inline-flex items-center gap-1 text-xs text-texto-suave hover:text-texto hover:underline',
    'pointer-coarse:min-h-11',
    className,
  );
}
